import React, { useState, useEffect } from "react";
import { Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import PermissionChecklist from "../components/permissions/PermissionChecklist";
import {
  createRole,
  isErpApiSuccess,
  listAdminUserPermissions,
} from "../erpClient";

// ── Unique color palette — one per role, never repeats ────────────────────────
export const ROLE_BADGE_COLORS = [
  { bg: "#cfe2ff", text: "#084298" },
  { bg: "#ffd6a0", text: "#7a3500" },
  { bg: "#d1e7dd", text: "#0a3622" },
  { bg: "#e2d9f3", text: "#3d0a91" },
  { bg: "#f8d7da", text: "#58151c" },
  { bg: "#caffef", text: "#00504a" },
  { bg: "#fff3cd", text: "#664d03" },
  { bg: "#c9d0f5", text: "#1a237e" },
  { bg: "#ffc8c8", text: "#7f0000" },
  { bg: "#c8f0da", text: "#064e1b" },
  { bg: "#f5c8f5", text: "#6a006a" },
  { bg: "#cff4fc", text: "#055160" },
  { bg: "#ffddb5", text: "#6d3200" },
  { bg: "#d4f3c0", text: "#1a4d00" },
  { bg: "#bfe8f9", text: "#013650" },
  { bg: "#ffc8e8", text: "#6b0035" },
];

export function getRoleBadgeColor(index) {
  return ROLE_BADGE_COLORS[index % ROLE_BADGE_COLORS.length];
}

function AddRole() {
  const navigate = useNavigate();
  const { buttonClass, cardClass } = useButtonTableStyle();

  const [roleName, setRoleName] = useState("");
  const [permissionCodes, setPermissionCodes] = useState([]);
  const [catalogCodes, setCatalogCodes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listAdminUserPermissions();
        if (cancelled) return;
        const codes = Array.isArray(res?.data?.permission_codes)
          ? res.data.permission_codes.map((c) => String(c).toUpperCase())
          : [];
        setCatalogCodes(codes);
      } catch {
        // ignore — panel still works with static catalog
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!roleName.trim()) {
      toast.error("Role name is required");
      return;
    }
    setSaving(true);
    try {
      const res = await createRole({
        name: roleName.trim(),
        permission_codes: permissionCodes,
      });
      if (isErpApiSuccess(res)) {
        toast.success(res?.message || `Role "${roleName.trim()}" created`);
        navigate("/roletable");
      } else {
        toast.error(res?.message || "Failed to create role");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to create role",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div
          className={`card w-100 position-relative overflow-hidden ${cardClass}`}
        >
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Add Role</h4>
          </div>

          <div className="card-body p-4">
            <form onSubmit={handleSave}>
              <div className="row g-4 mb-4">
                <div className="col-md-4">
                  <Form.Label className="fw-semibold">
                    Role Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    id="add-role-name"
                    type="text"
                    className={buttonClass}
                    placeholder="e.g. Sales Manager"
                    value={roleName}
                    onChange={(e) => setRoleName(e.target.value)}
                    required
                  />
                </div>
              </div>

              <hr className="mb-4" />
              <h5 className="fw-semibold mb-3">Permissions</h5>
              <PermissionChecklist
                value={permissionCodes}
                onChange={setPermissionCodes}
                catalogCodes={catalogCodes}
              />

              <div className="d-flex justify-content-end mt-4 gap-2">
                <button
                  type="button"
                  className={`btn btn-secondary ${buttonClass}`}
                  onClick={() => navigate("/roletable")}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  id="btn-save-role"
                  type="submit"
                  className={`btn btn-success ${buttonClass}`}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save Role"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddRole;
